import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { CANVAS_ZONES, PRESET_IDS, RoomZone, TOPBAR_ZONES, VisualizerLayout } from './layout.schema';
import { hasTopBar, presetLayout, zoneWidgets } from './layout.normalize';
import { PRESET_KEYS } from './layout.meta';

/** Whether a #rgb / #rrggbb colour is dark enough to need light marks on it. */
export function isDarkColor(color: string): boolean {
    const hex = color.trim().replace('#', '');
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex.slice(0, 6);
    if (!/^[0-9a-f]{6}$/i.test(full)) return false;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.45;
}

const W = 160;
const H = 100;

/**
 * A layout drawn small: the panel, the top bar, and a mark wherever widgets
 * sit. Drawn from the layout itself, so a preset's picture cannot drift from
 * what it does.
 */
export function MiniLayout({ layout, className }: { layout: VisualizerLayout; className?: string }) {
    const desk = layout.room.desktop;
    const panelW = Math.round((desk.panel.width / 1280) * W);
    const floating = desk.panel.style === 'floating';
    const inset = floating ? 4 : 0;
    const panelX = desk.panel.side === 'left' ? inset : W - panelW - inset;
    const canvasX = desk.panel.side === 'left' ? panelW + inset * 2 : 0;
    const canvasW = W - panelW - inset * 2;
    const bg = desk.canvas.background ?? '#ECEAE6';
    const dark = isDarkColor(bg);

    const bar = hasTopBar(layout);
    const barFloating = desk.topBar.style !== 'attached';
    const barY = barFloating ? 5 : 0;
    const barH = 11;
    const barX = canvasX + (barFloating ? 6 : 8);
    const barW = canvasW - (barFloating ? 12 : 16);

    const pills = (zone: RoomZone) => zoneWidgets(layout, zone).length;
    const corner = (zone: RoomZone) => {
        const n = pills(zone);
        if (!n) return null;
        const top = zone.includes('top');
        const y = top ? (bar ? barY + barH + 6 : 6) : H - 12;
        const w = Math.min(28, 7 * n + 3);
        const x = zone.endsWith('Left') ? canvasX + 6 : zone.endsWith('Right') ? canvasX + canvasW - 6 - w : canvasX + canvasW / 2 - w / 2;
        return <rect key={zone} x={x} y={y} width={w} height={7} rx={3.5} fill="#FFFFFF" opacity={0.95} stroke="rgba(0,0,0,0.12)" strokeWidth={0.6} />;
    };

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className={cn('block h-auto w-full', className)} aria-hidden>
            <rect x={0} y={0} width={W} height={H} fill={bg} />
            {/* a floor and a back wall, so the canvas reads as a room */}
            <polygon
                points={`${canvasX},${H} ${canvasX + canvasW},${H} ${canvasX + canvasW * 0.78},${H * 0.62} ${canvasX + canvasW * 0.22},${H * 0.62}`}
                fill={dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'}
            />
            <rect
                x={canvasX + canvasW * 0.22}
                y={H * 0.2}
                width={canvasW * 0.56}
                height={H * 0.42}
                fill={dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.45)'}
            />
            {/* panel */}
            <rect x={panelX} y={inset} width={panelW} height={H - inset * 2} rx={floating ? 4 : 0} fill="#FFFFFF" stroke="rgba(0,0,0,0.08)" strokeWidth={0.6} />
            <rect x={panelX + 5} y={inset + 5} width={panelW * 0.4} height={4} rx={2} fill="rgba(0,0,0,0.18)" />
            {Array.from({ length: 6 }).map((_, i) => {
                const cols = Math.max(1, Math.min(4, desk.panel.catalogColumns));
                const cw = (panelW - 10 - (cols - 1) * 3) / cols;
                const col = i % cols;
                const row = Math.floor(i / cols);
                return <rect key={i} x={panelX + 5 + col * (cw + 3)} y={inset + 16 + row * (cw + 3)} width={cw} height={cw} rx={1.5} fill="rgba(0,0,0,0.07)" />;
            })}
            {/* top bar */}
            {bar && (
                <g>
                    <rect x={barX} y={barY} width={barW} height={barH} rx={barFloating ? 3 : 0} fill="#FFFFFF" opacity={desk.topBar.style === 'glass' ? 0.82 : 1} stroke="rgba(0,0,0,0.08)" strokeWidth={0.5} />
                    {TOPBAR_ZONES.map((zone, i) => {
                        const n = pills(zone);
                        if (!n) return null;
                        const w = Math.min(barW / 3 - 6, 6 * n + 4);
                        const x = i === 0 ? barX + 4 : i === 1 ? barX + barW / 2 - w / 2 : barX + barW - 4 - w;
                        return <rect key={zone} x={x} y={barY + 3.5} width={w} height={4} rx={2} fill="rgba(0,0,0,0.22)" />;
                    })}
                </g>
            )}
            {CANVAS_ZONES.map(corner)}
        </svg>
    );
}

/** Whether a layout is still exactly the preset it was started from. */
export function isUnchangedPreset(layout: VisualizerLayout): boolean {
    if (!layout.preset) return false;
    const strip = ({ preset, ...rest }: VisualizerLayout) => JSON.stringify(rest);
    return strip(presetLayout(layout.preset)) === strip(layout);
}

export function PresetPicker({ layout, onPick }: { layout: VisualizerLayout; onPick: (id: string) => void }) {
    const { t } = useTranslation();
    const current = layout.preset;
    const edited = !isUnchangedPreset(layout);
    return (
        <div className="grid grid-cols-2 gap-2.5 @md:grid-cols-3">
            {PRESET_IDS.map((id) => {
                const active = current === id;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onPick(id)}
                        aria-pressed={active}
                        className={cn(
                            'group relative flex flex-col overflow-hidden rounded-lg border bg-background text-start transition-all hover:border-foreground/40',
                            active && 'border-foreground ring-1 ring-foreground',
                        )}
                    >
                        <MiniLayout layout={presetLayout(id)} />
                        <span className="flex items-center justify-between gap-2 px-2.5 pb-2 pt-1.5">
                            <span className="min-w-0">
                                <span className="block truncate text-xs font-medium text-foreground">{t(PRESET_KEYS[id]?.nameKey ?? id)}</span>
                                <span className="block truncate text-[11px] text-muted-foreground">{t(PRESET_KEYS[id]?.descKey ?? '')}</span>
                            </span>
                            {active && (edited
                                ? <span className="shrink-0 rounded-full border px-1.5 py-px text-[10px] text-muted-foreground">{t('VISUALIZER_LAYOUT.PRESETS.EDITED')}</span>
                                : <Check className="size-3.5 shrink-0 text-foreground" />)}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
